import getLocal from '@/app/utils/getLocal';
import MembersCompoment from '@/components/MembersDay';
import { MembersProvider, useMembersDispatch, useMembersStore } from '@/components/MembersDay/MembersProvider';
import { IPageStatus } from '@/components/MembersDay/type';
import PageLayout from '@/components/PageLayout';
import SEOlinksA from '@/components/SEOlinksA';
import { useLang } from '@/i18n';
import seoconfig from '@/seoconfig/appreciation-day';
import { getHostApi, redirectTo404 } from '@/utils';
import { getAppSession } from '@/utils/appGetToken';
import { onLogin } from '@/utils/cookie';
import { useGlobal } from '@/utils/useGlobal';
import getDefaultHeader from 'lowcode/utils/service/getDefaultHeader';
import type { GetServerSidePropsContext, NextPage } from 'next';
import { useEffect, useRef } from 'react';
import css from './index.module.scss';

const CountDown = ({ handleEnd }: { handleEnd?: () => void }) => {
    const { getSystemTime } = useGlobal();
    const { locale } = getLocal();
    const { timeLine, pageStatus } = useMembersStore();
    const dispatch = useMembersDispatch();
    const timer: any = useRef(null);
    const serverTime = useRef(getSystemTime());
    const clientTime = useRef(new Date().getTime());

    const handleVisibleChange = () => {
        if (window.document.visibilityState === 'visible') {
            getServiceTime();
        }
    };

    const getServiceTime = async () => {
        const API_HOST = getHostApi(locale).NEXT_PUBLIC_API_HOST;
        const headers: any = getDefaultHeader(locale);

        try {
            const spuData = await fetch(`${API_HOST}/serverTimestamp?locale=${locale}`, {
                headers,
            });
            const { data } = await spuData.json();
            serverTime.current = data?.timestamp;
        } catch (e) {
            console.log(e);
            serverTime.current = getSystemTime();
        }
    };

    const handleTimeOut = async () => {
        const nowTime = new Date().getTime();
        const intervalTime = nowTime - clientTime.current;
        // 倒计时执行间隔超过2s
        if (intervalTime > 2000) {
            await getServiceTime();
        } else {
            serverTime.current += intervalTime;
        }
        // 当前阶段
        let status = IPageStatus.TEASER;
        if (timeLine) {
            if (serverTime.current < timeLine.teaser.end) {
                status = IPageStatus.TEASER;
            } else if (serverTime.current < timeLine.activity.end) {
                status = IPageStatus.ACTIVITY;
            } else if (serverTime.current < timeLine.return.end) {
                status = IPageStatus.RETURN;
            } else {
                status = IPageStatus.END;
            }
        }
        if (status !== pageStatus) {
            dispatch({
                type: 'changePageStatus',
                payload: status,
            });
        }
        clientTime.current = nowTime;
        timer.current = setTimeout(handleTimeOut, 1000);
    };

    useEffect(() => {
        // 倒计时部分
        timer.current = setTimeout(handleTimeOut, 1000);
        // visiblity部分
        window.document.addEventListener('visibilitychange', handleVisibleChange);
        return () => {
            timer.current && clearTimeout(timer.current);
            window.document.removeEventListener('visibilitychange', handleVisibleChange);
        };
    }, []);

    useEffect(() => {
        timer.current && clearTimeout(timer.current);
        timer.current = setTimeout(handleTimeOut, 1000);
    }, [pageStatus]);

    return <div></div>;
};

const MembersDay: NextPage<any> = ({ isLogin, SEO, membersDayData, status, debug }: any) => {
    const { t } = useLang('membersday');
    const { isApp } = useGlobal();
    const { locale } = getLocal();

    if (isApp) {
        // efBridge.registerHandler('getAppMsg', (res) => {
        //     if (!res) return;
        //     const dataSet = {
        //         token: res.token.replace('Bearer ', ''),
        //         user: {},
        //     };
        //     onLogin(dataSet);
        // });
        const token = getAppSession();
        const dataSet = {
            token,
            user: {},
        };
        onLogin(dataSet);
    }

    const initState = {
        isLogin: isApp ? true : isLogin,
        timeLine: membersDayData.timeLine,
        protocol: membersDayData.protocol,
        pageStatus: status || IPageStatus.TEASER,
    };

    const isDebug = debug && Object.values(IPageStatus).includes(debug);

    if (isDebug) {
        initState.pageStatus = debug;
    }

    return (
        <>
            <SEO title={t('seo.title') || t('join_title')} description={t('seo.desc')} />
            <SEOlinksA locale={String(locale)} list={['us']} pathname={seoconfig.url} />
            <PageLayout isNoFixed hideFooter={isApp} hideHeader={isApp} theme="black" className={css.reviewPage}>
                <MembersProvider state={initState}>
                    {!isDebug && <CountDown />}
                    <MembersCompoment />
                </MembersProvider>
            </PageLayout>
        </>
    );
};

export default MembersDay;

export async function getServerSideProps({ locale, req, query }: GetServerSidePropsContext) {
    const whiteLocaleList = ['us', 'ca', 'eu', 'uk', 'de', 'fr', 'it', 'es', 'jp', 'kr', 'za', 'au'];

    // 配置页面基本信息
    let membersDayData = null;
    try {
        membersDayData = require(`@/data/membersday/${locale}.js`);
    } catch (err) {
        membersDayData = require(`@/data/membersday/us.js`);
        // throw new Error('读取/data/membersday/us.js失败');
    }

    // 时间节点判断 正式环境未开始重定向404
    const { debug } = query;
    const time = new Date().getTime();
    if (process.env.NEXT_PUBLIC_MODY === 'prd') {
        if (time < membersDayData.timeLine?.teaser.start && !debug) {
            return redirectTo404();
        }
        // debug = '';
    }
    // 当前阶段
    let status = IPageStatus.TEASER;
    if (membersDayData.timeLine) {
        if (time < membersDayData.timeLine.teaser.end) {
            status = IPageStatus.TEASER;
        } else if (time < membersDayData.timeLine.activity.end) {
            status = IPageStatus.ACTIVITY;
        } else if (time < membersDayData.timeLine.return.end) {
            status = IPageStatus.RETURN;
        } else {
            status = IPageStatus.END;
        }
    }

    // 非白名单404
    if (!whiteLocaleList.includes(locale as string)) {
        return redirectTo404();
    }

    // 获取登陆信息
    const userInfoJson = req.cookies?.userInfo || '';
    let isLogin = false;
    try {
        const userInfo = JSON.parse(userInfoJson);
        isLogin = !!userInfo.token;
    } catch {}

    return {
        props: {
            isLogin,
            membersDayData,
            status,
            debug: debug || '',
        },
    };
}
